import {
  Button,
  Cell,
  Grid,
  HFlow,
  ModalMountTarget,
  Text,
  TextField,
  Theme,
  useLocale,
  useStyles,
  VFlow,
} from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import { difference, union } from 'lodash'
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { normalizeText } from 'util/string/string-normalizeText'

import { Aggregators } from '../aggregator/Aggregators'
import { Aggregator } from '../aggregator/model'
import { Box } from '../box/Box'
import { Droppable, DroppableProps } from '../droppable/Droppable'
import { DroppableFilter } from '../droppable/types/Filter'
import { KeyMap } from '../model'
import { FilterValuesTags } from './FilterValuesTags'
import { BoardField, FieldFiltersByKey, FieldValuesByKey, PivotTableBoardOrigin, RowColumnKeys } from './model'
import { renderClearTableModal } from './render'
import { useOnKeyNav } from './useOnKeyNav'
import { getInitialKeys, handleTagFilterRemove, initializeActiveFilters } from './utils'

interface BoardAggregatorProps<T extends object> {
  onChange: (aggregator: Aggregator) => void
  onKeyChange: (key: keyof T) => void
  value: Aggregator
  key: keyof T
}

interface PivotTableBoardProps<T extends object> {
  keys: FieldValuesByKey<T>
  keyMapping: KeyMap<T>
  numberKeys: Array<keyof T>
  isBuilding: boolean
  onSubmit: (values: RowColumnKeys<T>, filterValues: FieldFiltersByKey<T>) => void
  onReset: () => void
  aggregator: BoardAggregatorProps<T>
  initialFields?: Array<BoardField<T>>
  enableSearchField: boolean
}

export function PivotTableBoard<T extends object>(props: Readonly<PivotTableBoardProps<T>>) {
  const {
    keys,
    keyMapping,
    onSubmit,
    numberKeys,
    isBuilding,
    onReset,
    aggregator,
    initialFields,
    enableSearchField,
  } = props

  const { pivotTableBoard: pivotTableBoardLabels } = useLocale()

  const allFiltersByKey = useMemo(() => new Map(Array.from(keys).map(([key, values]) => [key, new Set(values)])), [
    keys,
  ])

  const [filterState, setFilterState] = useState<FieldFiltersByKey<T>>(allFiltersByKey)

  const [initialized, setInitialized] = useState(false)
  const [rowKeys, setRowKeys] = useState<Array<keyof T>>([])
  const [columnKeys, setColumnKeys] = useState<Array<keyof T>>([])
  const [availableKeys, setAvailableKeys] = useState<Array<keyof T>>([])
  const [availableKeysSearch, setAvailableKeysSearch] = useState('')

  useEffect(() => {
    if (!initialized) {
      const { initialRowKeys, initialColumnKeys, initialAvailableKeys } = getInitialKeys(keys, initialFields)

      setFilterState(initializeActiveFilters(allFiltersByKey, keys, initialFields))

      setRowKeys(initialRowKeys)
      setColumnKeys(initialColumnKeys)
      setAvailableKeys(initialAvailableKeys)
      setInitialized(true)
    }
  }, [initialized, keys, allFiltersByKey, initialFields])

  const { classes } = useStyles(createStyles)

  const handleGenerateTableClick = useCallback(() => onSubmit([rowKeys, columnKeys], filterState), [
    columnKeys,
    filterState,
    onSubmit,
    rowKeys,
  ])

  const handleClearFilters = useCallback(() => setFilterState(allFiltersByKey), [allFiltersByKey])

  const handleTableReset = useCallback(() => {
    setColumnKeys([])
    setRowKeys([])
    handleClearFilters()
    onReset()
    setAvailableKeys(Array.from(keys.keys()))
  }, [handleClearFilters, onReset, keys])

  const filteredAvailableKeys = useMemo(() => {
    if (!enableSearchField || !availableKeysSearch) return availableKeys

    const normalizedSearch = normalizeText(availableKeysSearch).toLowerCase()

    return availableKeys.filter((key) => {
      const label = keyMapping.get(key)?.keyName ?? String(key)
      return normalizeText(label).toLowerCase().includes(normalizedSearch)
    })
  }, [availableKeys, availableKeysSearch, keyMapping, enableSearchField])

  const onKeyNav = useOnKeyNav({
    setColumnKeys,
    setRowKeys,
    setAvailableKeys,
    columnKeys,
    rowKeys,
    availableKeys,
  })

  const handleFilterUpdate = useCallback(
    (key: keyof T, filter: Set<string>) => {
      setFilterState(() => {
        const newState = new Map(filterState)
        if (filter.size === 0) {
          newState.delete(key)
        } else {
          newState.set(key, filter)
        }
        return newState
      })
    },
    [filterState]
  )

  const handleAvailableKeysUpdate = useCallback(
    (newFilteredKeys: Array<keyof T>) => {
      if (!enableSearchField || !availableKeysSearch) {
        setAvailableKeys(newFilteredKeys)
        return
      }

      setAvailableKeys((prevKeys) => {
        const unaffectedKeys = difference(prevKeys, filteredAvailableKeys)

        return union(unaffectedKeys, newFilteredKeys)
      })
    },
    [availableKeysSearch, enableSearchField, filteredAvailableKeys]
  )

  const handleRemoveTag = useCallback(
    (key: keyof T, value: string) => {
      handleFilterUpdate(key, handleTagFilterRemove(key, value, filterState))
    },
    [filterState, handleFilterUpdate]
  )

  const filter: DroppableFilter<T> = useMemo(
    () => ({
      handleUpdate: handleFilterUpdate,
      keys: keys,
      state: filterState,
    }),
    [handleFilterUpdate, keys, filterState]
  )

  const { PILOTO_RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL_ENABLED } = useFlags()

  const renderFiltersBox = ({ scrollable }: Partial<DroppableProps<T, PivotTableBoardOrigin>>) => (
    <Box label={pivotTableBoardLabels.availableFields}>
      <>
        {enableSearchField && (
          <VFlow style={classes.textFieldSearch}>
            <TextField
              placeholder='Busque por um campo disponível'
              icon='zoomOutline'
              onChange={(e) => setAvailableKeysSearch(e.target.value.trim())}
            />
          </VFlow>
        )}
        <Droppable<T, PivotTableBoardOrigin>
          data-testid='droppable-available'
          keyState={filteredAvailableKeys}
          filter={filter}
          keyMapping={keyMapping}
          handleKeyUpdate={handleAvailableKeysUpdate}
          onKeyNav={onKeyNav}
          name='filter'
          accept='filter'
          scrollable={scrollable}
        />
      </>
    </Box>
  )
  const columnsBox = (
    <Box label={pivotTableBoardLabels.columns} icon='hamburguerMenu' rotation='90'>
      <Droppable<T, PivotTableBoardOrigin>
        data-testid='droppable-column'
        keyState={columnKeys}
        filter={filter}
        keyMapping={keyMapping}
        handleKeyUpdate={setColumnKeys}
        onKeyNav={onKeyNav}
        name='column'
        accept='filter'
      />
    </Box>
  )
  const rowsBox = (
    <Box label={pivotTableBoardLabels.rows} icon='hamburguerMenu'>
      <Droppable<T, PivotTableBoardOrigin>
        data-testid='droppable-row'
        keyState={rowKeys}
        filter={filter}
        keyMapping={keyMapping}
        handleKeyUpdate={setRowKeys}
        onKeyNav={onKeyNav}
        name='row'
        accept='filter'
      />
    </Box>
  )
  const aggregatorsBox = (
    <Box label={pivotTableBoardLabels.kindOfPresentation}>
      <div className={classes.aggregatorsContainer}>
        <Aggregators
          numberKeys={numberKeys}
          keyMapping={keyMapping}
          handleAggregatorChange={aggregator.onChange}
          handleAggregatorKeyChange={aggregator.onKeyChange}
          aggregator={aggregator.value}
          aggregatorKey={aggregator.key}
        />
      </div>
    </Box>
  )

  return (
    <DndProvider backend={HTML5Backend}>
      <Grid>
        {PILOTO_RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL_ENABLED ? (
          <>
            <Cell md={6} sm={12} xs={12}>
              {renderFiltersBox({ scrollable: true })}
            </Cell>
            <Cell md={6} sm={12} xs={12}>
              <VFlow style={classes.selectedFieldsContainer}>
                {columnsBox}
                {rowsBox}
              </VFlow>
            </Cell>
            <Cell md={12} sm={12} xs={12}>
              {aggregatorsBox}
            </Cell>
          </>
        ) : (
          <>
            <Cell md={6} sm={12} xs={12}>
              {renderFiltersBox({ scrollable: false })}
            </Cell>
            <Cell md={6} sm={12} xs={12}>
              {columnsBox}
            </Cell>
            <Cell md={6} sm={12} xs={12}>
              {rowsBox}
            </Cell>
            <Cell md={6} sm={12} xs={12}>
              {aggregatorsBox}
            </Cell>
          </>
        )}
        <Cell sm={12}>
          <div className={classes.filtersContainer}>
            <Grid wrap alignItems='center'>
              <Cell size={10}>
                <HFlow alignItems='center'>
                  <Text fontWeight='bold'>{pivotTableBoardLabels.appliedFilters}</Text>
                  <FilterValuesTags
                    filterState={filterState}
                    keys={keys}
                    keyMapping={keyMapping}
                    onRemoveTag={handleRemoveTag}
                  />
                </HFlow>
              </Cell>
              <Cell size={2}>
                <Button
                  onClick={handleClearFilters}
                  size='small'
                  kind='normal'
                  skin='outline'
                  style={{ float: 'right' }}
                >
                  {pivotTableBoardLabels.clearFilters}
                </Button>
              </Cell>
            </Grid>
          </div>
        </Cell>
        <Cell size={12}>
          <HFlow justifyContent='flex-end'>
            <Button
              kind='normal'
              size='medium'
              onClick={renderClearTableModal(pivotTableBoardLabels, handleTableReset)}
            >
              {pivotTableBoardLabels.clearTable}
            </Button>
            <ModalMountTarget />
            <Button
              kind='primary'
              size='medium'
              onClick={handleGenerateTableClick}
              loading={isBuilding}
              disabled={isBuilding}
            >
              {pivotTableBoardLabels.generateTable}
            </Button>
          </HFlow>
        </Cell>
      </Grid>
    </DndProvider>
  )
}

const createStyles = (theme: Theme) => ({
  aggregatorsContainer: {
    padding: '0.75rem',
    margin: '0.25rem',
  } as CSSProperties,
  filtersContainer: {
    border: `1px solid ${theme.pallete.divider}`,
    padding: '0.75rem 1.25rem',
  } as CSSProperties,
  selectedFieldsContainer: {
    height: '100%',
    '> div': {
      height: '100%',
    },
  } as CSSProperties,
  textFieldSearch: {
    padding: '0.5rem',
    borderBottom: `1px solid ${theme.pallete.divider}`,
    boxShadow: `0 3px 4px -0.75px ${theme.pallete.divider}`,
    position: 'relative',
    zIndex: 1,
    marginBottom: '0.1rem',
  } as CSSProperties,
})
